import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Button,
  CardContent,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
} from '@mui/material';
import BackupIcon from '@mui/icons-material/Backup';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import {
  useCloneRelease,
  useDeleteChecklist,
  useReleaseChecklist,
  useSaveChecklist,
} from '../../../hooks/useReleaseChecklist';
import {
  ActionRow,
  DangerOutlinedButton,
  Field,
  Heading,
  Section,
} from './styles';
import { NameInputDialog } from './NameInputDialog';
import { ConfirmDialog } from './ConfirmDialog';
import { groupReleases, sortReleasesDesc } from './releaseGrouping';

interface Props {
  releases: string[];
  selectedRelease: string | null;
  onSelect: (release: string) => void;
  // Optional extras rendered inside the card, below the action row —
  // currently used to host the Filters panel so it lives in the same
  // visual container as the release picker.
  children?: React.ReactNode;
}

// The day-to-day release management surface: pick a release from the
// grouped dropdown, then Create / Copy / Delete one. Owns the dialogs for
// each destructive or name-prompting action, since they share state with
// the action buttons that open them.
export const ManageReleasesCard = ({
  releases,
  selectedRelease,
  onSelect,
  children,
}: Props) => {
  const alertApi = useApi(alertApiRef);
  const [newName, setNewName] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copyName, setCopyName] = useState('');
  const [copyOpen, setCopyOpen] = useState(false);
  const saveChecklist = useSaveChecklist();
  const deleteChecklist = useDeleteChecklist();
  const cloneRelease = useCloneRelease();

  const { data: defaults, isFetching: defaultsLoading } =
    useReleaseChecklist('_default');

  const sortedReleases = useMemo(() => sortReleasesDesc(releases), [releases]);
  const groupedReleases = useMemo(
    () => groupReleases(sortedReleases),
    [sortedReleases],
  );

  const trimmed = newName.trim();
  const alreadyExists = trimmed !== '' && releases.includes(trimmed);

  const trimmedCopy = copyName.trim();
  const copyAlreadyExists =
    trimmedCopy !== '' && releases.includes(trimmedCopy);
  const copySameAsSource =
    trimmedCopy !== '' &&
    selectedRelease !== null &&
    trimmedCopy === selectedRelease;

  const closeCopyDialog = () => {
    setCopyOpen(false);
    setCopyName('');
  };

  const handleCopy = async () => {
    if (!selectedRelease) return;
    try {
      const result = await cloneRelease.mutateAsync({
        release: selectedRelease,
        newRelease: trimmedCopy,
      });
      onSelect(result.dest);
      closeCopyDialog();
      alertApi.post({
        message: `Copied ${result.source} to ${result.dest} (${
          result.items
        } item${result.items === 1 ? '' : 's'}, regions reset)`,
        severity: 'success',
      });
    } catch (e) {
      alertApi.post({
        message: `Failed to copy release: ${(e as Error).message}`,
        severity: 'error',
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedRelease) return;
    try {
      await deleteChecklist.mutateAsync({ release: selectedRelease });
      onSelect('');
      alertApi.post({
        message: `Deleted release ${selectedRelease}`,
        severity: 'success',
      });
    } catch (e) {
      alertApi.post({
        message: `Failed to delete release: ${(e as Error).message}`,
        severity: 'error',
      });
    } finally {
      setConfirmDelete(false);
    }
  };

  const closeCreateDialog = () => {
    setCreateOpen(false);
    setNewName('');
  };

  const handleCreate = async () => {
    const seedItems =
      defaults?.items?.map(s => ({
        kind: s.kind,
        text: s.text,
        completed: s.completed,
        repo: s.repo,
        order: s.order,
      })) ?? [];
    try {
      await saveChecklist.mutateAsync({
        release: trimmed,
        items: seedItems,
      });
      onSelect(trimmed);
      closeCreateDialog();
      alertApi.post({
        message: `Created release ${trimmed}`,
        severity: 'success',
      });
    } catch (e) {
      alertApi.post({
        message: `Failed to create release: ${(e as Error).message}`,
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Section>
        <CardContent>
          <Heading variant="h6">Manage releases</Heading>
          <Field variant="outlined" size="small">
            <InputLabel id="release-select-label">
              Available releases
            </InputLabel>
            <Select
              labelId="release-select-label"
              label="Available releases"
              value={selectedRelease ?? ''}
              onChange={e => onSelect(e.target.value as string)}
            >
              {sortedReleases.length === 0 && (
                <MenuItem value="" disabled>
                  <em>No releases yet</em>
                </MenuItem>
              )}
              {groupedReleases.prod.length > 0 && [
                <ListSubheader key="prod-head">PROD</ListSubheader>,
                ...groupedReleases.prod.map(r => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                )),
              ]}
              {groupedReleases.uat.length > 0 && [
                <ListSubheader key="uat-head">UAT</ListSubheader>,
                ...groupedReleases.uat.map(r => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                )),
              ]}
              {groupedReleases.other.length > 0 && [
                <ListSubheader key="other-head">Other</ListSubheader>,
                ...groupedReleases.other.map(r => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                )),
              ]}
            </Select>
          </Field>
          <ActionRow>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={defaultsLoading || saveChecklist.isPending}
              onClick={() => setCreateOpen(true)}
            >
              {saveChecklist.isPending ? 'Creating…' : 'Create'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={!selectedRelease || cloneRelease.isPending}
              onClick={() => setCopyOpen(true)}
            >
              {cloneRelease.isPending ? 'Copying…' : 'Copy'}
            </Button>
            <DangerOutlinedButton
              size="small"
              variant="outlined"
              disabled={!selectedRelease || deleteChecklist.isPending}
              onClick={() => setConfirmDelete(true)}
            >
              {deleteChecklist.isPending ? 'Deleting…' : 'Delete'}
            </DangerOutlinedButton>
          </ActionRow>
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<BackupIcon />}
            component={RouterLink}
            to={
              selectedRelease
                ? `/release-checklist-backups#?release=${encodeURIComponent(
                    selectedRelease,
                  )}`
                : '/release-checklist-backups'
            }
            style={{ marginTop: 8 }}
            fullWidth
          >
            View backups
          </Button>
          {children}
        </CardContent>
      </Section>

      <NameInputDialog
        open={createOpen}
        title="Create release"
        body="Seed a new release checklist from the default template. The name should match the Bitbucket tag."
        inputLabel="New release name"
        inputPlaceholder="e.g. 2603.0-prod"
        value={newName}
        onChange={setNewName}
        helperText="Make sure this matches a Bitbucket tag"
        errorText={
          alreadyExists ? 'A release with this name already exists' : undefined
        }
        submitLabel="Create"
        submitPendingLabel="Creating…"
        pending={saveChecklist.isPending}
        externallyDisabled={trimmed === '' || defaultsLoading}
        onClose={closeCreateDialog}
        onSubmit={handleCreate}
      />

      <NameInputDialog
        open={copyOpen}
        title="Copy release"
        body={
          <>
            Clone <strong>{selectedRelease}</strong>'s sections and steps into a
            new release. Region check boxes are reset on the copy.
          </>
        }
        inputLabel="New release name"
        inputPlaceholder="e.g. 2603.0-prod"
        value={copyName}
        onChange={setCopyName}
        helperText="Sections + steps will be cloned; region checkboxes are reset"
        errorText={
          copyAlreadyExists
            ? 'A release with this name already exists'
            : copySameAsSource
            ? 'Must differ from the source release'
            : undefined
        }
        submitLabel="Copy"
        submitPendingLabel="Copying…"
        pending={cloneRelease.isPending}
        externallyDisabled={!selectedRelease || trimmedCopy === ''}
        onClose={closeCopyDialog}
        onSubmit={handleCopy}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete release?"
        body={
          <>
            Permanently delete <strong>{selectedRelease}</strong> and all its
            checklist items? This can't be undone.
          </>
        }
        confirmLabel="Delete"
        confirmPendingLabel="Deleting…"
        pending={deleteChecklist.isPending}
        variant="danger"
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
      />
    </>
  );
};
